import React, { useCallback, useMemo } from 'react';
// MUI
import { Box, Button, Grid, Typography, Menu, MenuItem } from '@mui/material';
// Icons
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
// Helpers
import { EmojiFlagMapType } from '../../../services/partnerResourcesHelpers';
// Interfaces
import { PartnerResource } from '../../../interfaces/partnerResources.interface';
// Hooks
import { useAppSelector } from '../../../app/hooks';

import { useTheme } from '@mui/material/styles';

import Flag from '../PartnerResourcesFlags';
import { USER_TYPES } from '../../../services/user';
import { View } from '@aws-amplify/ui-react';
import { useWorkflowMap } from '../../../hooks/useWorkflowMap';

const MPABestPractices = ({
  filteredSections,
  handleDownloadPartnerResource,
  iyunoLogo,
}: {
  handleDownloadPartnerResource: (section: PartnerResource) => void;
  filteredSections: PartnerResource[];
  iyunoLogo: string;
}) => {
  const workflowMap = useWorkflowMap();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuIndex, setMenuIndex] = React.useState<number | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(index);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const { user } = useAppSelector(state => state.user);
  const theme = useTheme();

  const sumOfDownloads = useCallback((items: PartnerResource[]) => {
    return items.reduce((sum, item) => sum + (item.metadata?.length || 0), 0);
  }, []);

  const formattedFilteredSections = useMemo(
    () =>
      Object.values(
        filteredSections.reduce((acc: Record<string, PartnerResource[]>, item: PartnerResource) => {
          const title = item.title as EmojiFlagMapType;
          if (!acc[title]) {
            acc[title] = [];
          }
          acc[title].push(item);
          return acc;
        }, {}),
      )
        .map((group) =>
          group.sort(
            (a, b) =>
              (workflowMap[b.description]?.sortOrder || 0) -
              (workflowMap[a.description]?.sortOrder || 0),
          ),
        )
        .sort((a, b) => {
          const nativeNameA = a[0].subDescription;
          const nativeNameB = b[0].subDescription;
          // Check if either is "English" and prioritize it
          if (nativeNameA === 'English') return -1;
          if (nativeNameB === 'English') return 1;
          // Otherwise, sort alphabetically
          return nativeNameA.localeCompare(nativeNameB);
        }),
    [filteredSections],
  );

  return (
    <Box sx={{ padding: 3 }}>
      <View
        style={{
          fontWeight: 600,
          marginBottom: '1rem',
          color: '#333',
        }}
      >
        <Typography variant="body1" paragraph sx={{ mb: 3, color: '#666', px: 1 }}>
          Download the MPA Content Security Best Practices in the following languages.
        </Typography>
      </View>

      <Grid container spacing={3}>
        {formattedFilteredSections.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} lg={4} key={index} sx={{ minWidth: '375px' }}>
            {/* Card */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: 2,
                minHeight: '90px',
                border: '1px solid #eceef0',
                borderRadius: 2,
                backgroundColor: '#fafbfc',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#d9dde1',
                  backgroundColor: '#f8f9fb',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: '150px' }}>
                  {/* Flag + Language */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      minWidth: 0,
                    }}
                  >
                    {/* Flag Icon */}
                    <Box sx={{ mt: -0.25, mr: 1 }}>
                      <Flag language={item[0].title as EmojiFlagMapType} />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#4a4a4a',
                        fontWeight: 500,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        '&:hover': {
                          color: '#333333',
                        },
                      }}
                    >
                      {item[0].subDescription}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    position: 'relative',
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(event) => handleOpenMenu(event, index)}
                    sx={{
                      textTransform: 'none',
                      color: '#fff',
                      minWidth: '80px',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        color: '#fff',
                      },
                    }}
                  >
                    Download
                  </Button>
                  <Menu anchorEl={anchorEl} open={menuIndex === index} onClose={handleCloseMenu}>
                    {item.map((version, vIndex) => (
                      <MenuItem
                        key={vIndex}
                        onClick={() => {
                          handleDownloadPartnerResource(version);
                          handleCloseMenu();
                        }}
                      >
                        {workflowMap[version.description]?.version || version.description}
                      </MenuItem>
                    ))}
                  </Menu>
                  {user?.type === USER_TYPES.TPN_ADMIN && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: -25,
                        fontWeight: 500,
                        color: theme.palette.customThemeColors?.darkGrey,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mr: 1,
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 'small', verticalAlign: 'middle' }} />
                      {sumOfDownloads(item)} Downloads
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mt: 3,
          px: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: '#999' }}>
          Translations sponsored by
        </Typography>
        <img
          src={iyunoLogo}
          alt="Iyuno"
          style={{ height: '16px', verticalAlign: 'middle' }}
        />
      </Box>
    </Box>
  );
};

export default MPABestPractices;
